/**
 * O cartão de perfil que abre ao carregar no nome de alguém — no chat ou na
 * lista de membros.
 *
 * Um cartão só, usado pelos dois sítios, e não dois parecidos: a lista de
 * membros já tinha o dela, e a primeira versão deste ecrã ia ganhar outro no
 * chat. Duas cópias divergem sempre — o botão novo entra numa e esquece a
 * outra, e o utilizador aprende que carregar no nome faz coisas diferentes
 * conforme o sítio onde carregou.
 *
 * Vive num PORTAL para o `body`. A razão é mecânica: o contentor das mensagens
 * tem `overflow` e a lista de membros também, portanto um cartão posicionado lá
 * dentro era cortado ao meio na primeira e na última linha — precisamente onde
 * mais se carrega. Posicionado a partir do rectângulo do elemento clicado,
 * escapa aos dois.
 */

import type { PublicUser } from "@greenvoice/shared";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { useVoiceSnapshot } from "../hooks/useVoice";
import { VOLUME_MAX } from "../lib/userVolumes";
import { voiceEngine } from "../lib/voice";
import { gatewayClient, useStore } from "../store/store";
import { AddFriendButton } from "./AddFriendButton";
import { IconBell, IconHeadphones, IconLock, IconMic, IconSend, IconSpeaker, IconSpeakerOff } from "./Icons";
import { ProfileCard } from "./ProfileCard";
import { UserCardRoles } from "./UserCardRoles";

/** Folga entre o cartão e o elemento que o abriu. */
const GAP = 8;
/** Margem mínima ao bordo do ecrã — um cartão colado ao bordo lê-se mal. */
const EDGE = 8;
const CARD_WIDTH = 260;

/**
 * Onde o cartão se agarra. Quase sempre é o ELEMENTO em que se carregou, que dá
 * duas coisas de uma vez: a posição e o "carregar outra vez aqui não fecha para
 * reabrir logo a seguir". Mas o escritório 2D é uma tela — carregar num avatar
 * não tem elemento nenhum por trás, só um ponto no ecrã. Aceitar os dois é o que
 * permite continuar a haver UM cartão, em vez de nascer um segundo parecido só
 * porque a origem do clique era diferente.
 */
export type UserCardAnchor = HTMLElement | { x: number; y: number };

/** O rectângulo de onde partir. Um ponto é um rectângulo de largura zero — o
 *  resto da conta de posicionamento não precisa de saber a diferença. */
function anchorBox(anchor: UserCardAnchor): { left: number; right: number; top: number } {
  return "getBoundingClientRect" in anchor
    ? anchor.getBoundingClientRect()
    : { left: anchor.x, right: anchor.x, top: anchor.y };
}

interface UserCardProps {
  user: PublicUser;
  /** O elemento — ou o ponto — em que se carregou. Ver `UserCardAnchor`. */
  anchor: UserCardAnchor;
  /** Cor do cargo, já calculada por quem abriu (o chat e a lista usam a mesma). */
  color?: string | null;
  /** Alcunha do servidor, se houver — o cartão mostra o nome como ele aparece. */
  displayName?: string | null;
  onClose(): void;
}

export function UserCard({ user, anchor, color, displayName, onClose }: UserCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  // Posiciona-se DEPOIS de medir o cartão real (o conteúdo muda de altura: com
  // volume, sem volume, com erro). Calcular antes obrigava a fixar uma altura,
  // e uma altura fixa é a origem do cartão que sai do ecrã.
  useLayoutEffect(() => {
    const card = cardRef.current;
    if (!card) return;
    const from = anchorBox(anchor);
    const { width, height } = card.getBoundingClientRect();

    // Prefere-se a direita (é o lado onde há espaço no chat); se não coubesse,
    // vai para a esquerda do elemento, que é o caso da lista de membros.
    const right = from.right + GAP;
    const left = right + width + EDGE > window.innerWidth ? from.left - GAP - width : right;
    const top = Math.min(Math.max(EDGE, from.top), window.innerHeight - height - EDGE);
    const next = { left: Math.max(EDGE, left), top: Math.max(EDGE, top) };
    // Só mexe se MUDOU. Um âncora-ponto é um objecto novo a cada render de quem
    // abriu; sem esta comparação, `setPos` re-renderizava, o efeito voltava a
    // correr e o cartão entrava num ciclo que nunca assenta.
    setPos((current) => (current && current.left === next.left && current.top === next.top ? current : next));
  }, [anchor]);

  // Fechar: Esc, carregar fora, ou o chão mexer-se debaixo do cartão (scroll /
  // redimensionar). No scroll fecha-se em vez de perseguir o âncora — perseguir
  // um alvo dentro de uma lista virtual é como se ganham cartões a flutuar
  // sobre a pessoa errada.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    const onPointer = (event: MouseEvent) => {
      const target = event.target as Node;
      if (cardRef.current?.contains(target)) return;
      // O próprio botão que abriu: deixa-se passar para o `onClick` dele poder
      // alternar. Sem isto, fechava-se aqui e reabria-se logo a seguir. Um
      // âncora-ponto não contém nada — quem abre pela tela trata do seu próprio
      // "carregar outra vez" no sítio onde faz o acerto do clique.
      if ("contains" in anchor && anchor.contains(target)) return;
      onClose();
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    window.addEventListener("resize", onClose);
    // `capture`: o scroll acontece no contentor das mensagens, não na janela, e
    // eventos de scroll não borbulham.
    window.addEventListener("scroll", onClose, true);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
      window.removeEventListener("resize", onClose);
      window.removeEventListener("scroll", onClose, true);
    };
  }, [anchor, onClose]);

  const name = displayName ?? user.displayName ?? user.username;

  return createPortal(
    <div
      ref={cardRef}
      className="user-card"
      role="dialog"
      aria-label={`Perfil de ${name}`}
      style={{
        left: pos?.left ?? 0,
        top: pos?.top ?? 0,
        width: CARD_WIDTH,
        // Enquanto não está medido fica invisível em vez de aparecer no canto
        // superior esquerdo e saltar para o sítio certo no frame seguinte.
        visibility: pos ? "visible" : "hidden",
      }}
    >
      {/* O DESENHO do perfil está no `ProfileCard`, partilhado com a
          pré-visualização do editor — o que é deste popover são as acções e o
          posicionamento. */}
      <ProfileCard user={user} displayName={displayName} color={color}>
        <UserCardActions user={user} onClose={onClose} />
        {/* Dar cargos aqui e não só nas definições: o gesto natural é carregar
            na pessoa e dizer o que ela é. Só aparece a quem PODE — a regra é a
            mesma que o servidor aplica, ver `UserCardRoles`. */}
        <UserCardRoles user={user} onClose={onClose} />
      </ProfileCard>
    </div>,
    document.body,
  );
}

/** O que se pode FAZER com a pessoa. Separado do cartão porque é a parte que
 *  tem estado (envio a decorrer, volume) e o cabeçalho não tem nenhum. */
function UserCardActions({ user, onClose }: { user: PublicUser; onClose(): void }) {
  const selfId = useStore((s) => s.self?.id ?? null);
  const openDirectMessage = useStore((s) => s.openDirectMessage);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isSelf = selfId === user.id;

  // Chamar a atenção só existe se estivermos os DOIS no escritório: é lá que o
  // avatar estremece. Fora dele o botão prometia um efeito que ninguém veria — o
  // servidor recusa na mesma, mas um botão que não faz nada é pior que ausente.
  const spaceId = useStore((s) => s.activeServerId);
  const bothInOffice = useStore(
    (s) => s.viewMode === "office" && s.activeServerId !== null && Boolean(s.positions[s.activeServerId]?.[user.id]),
  );
  // Uma ligação de cada vez: com uma a tocar ou em curso, o botão diz porquê.
  const emLigacao = useStore((s) => s.ligacao !== null);

  const message = useCallback(async () => {
    setSending(true);
    setError(null);
    try {
      await openDirectMessage(user.id);
      onClose();
    } catch (err) {
      // Falhar em silêncio aqui é o pior dos mundos: carrega-se no botão, nada
      // acontece, e não há nada no ecrã que diga porquê (bloqueado, sem rede).
      setError((err as Error).message);
      setSending(false);
    }
  }, [openDirectMessage, onClose, user.id]);

  return (
    <>
      {/* Em PRIMEIRO, e de propósito. Este botão só existe enquanto a pessoa
          ainda não é amiga — para toda a gente com quem já falo, o cartão fica
          exactamente como estava. E no cartão em que ele APARECE, adicionar é o
          motivo de se ter carregado no nome; posto por baixo de três botões
          voltava a ser o «botão escondido, indistinguível de um botão que nunca
          foi feito» que já custou a voz privada uma vez.

          É o MESMO componente que a lista do escritório usa: a regra de quando
          aparece e o que faz vive num sítio só (ver `AddFriendButton`), que é o
          contrário do que aconteceu ao botão do microfone. */}
      <AddFriendButton user={user} className="user-card__action" />
      {isSelf ? null : (
        <button type="button" className="user-card__action" onClick={() => void message()} disabled={sending}>
          <IconSend />
          {sending ? "A abrir…" : "Enviar mensagem"}
        </button>
      )}
      {isSelf || !bothInOffice || !spaceId ? null : (
        <button
          type="button"
          className="user-card__action"
          onClick={() => {
            gatewayClient()?.poke(spaceId, user.id);
            // Fecha: o efeito do toque é o avatar a estremecer no mapa, e o
            // cartão estava precisamente por cima dele.
            onClose();
          }}
        >
          <IconBell />
          Chamar atenção
        </button>
      )}
      {/* Ligar: a mesma condição do toque — os dois no escritório. A ligação em si
          não depende de onde estão; só COMEÇA pelo boneco de alguém. */}
      {isSelf || !bothInOffice || !spaceId ? null : (
        <button
          type="button"
          className="user-card__action"
          disabled={emLigacao}
          onClick={() => {
            gatewayClient()?.ligar(spaceId, user.id);
            onClose();
          }}
        >
          <IconHeadphones />
          {emLigacao ? "Já estás numa ligação" : "Ligar"}
        </button>
      )}
      {error ? <span className="user-card__error">{error}</span> : null}
      <PrivateVoice userId={user.id} isSelf={isSelf} />
      <UserVolume userId={user.id} isSelf={isSelf} />
    </>
  );
}

/**
 * Falar só para uma pessoa da chamada — e o botão que a tira de lá.
 *
 * A voz privada é feita de PERMISSÕES de subscrição na sala do LiveKit: sem uma
 * chamada partilhada não há áudio nenhum para apontar a ninguém. Por isso o
 * botão está sempre lá mas só FUNCIONA na mesma chamada.
 *
 * Escondê-lo fora dela era o que fazia antes, e custou exactamente o que devia
 * custar: um botão escondido é indistinguível de um botão que nunca foi feito —
 * procura-se, não se acha, e conclui-se que a funcionalidade não existe. Um
 * botão inerte é pior que ausente SÓ enquanto não diz porquê; a dizer, passa a
 * ser a única coisa no ecrã que ensina a condição. Daí o `disabled` com a razão
 * escrita ao lado, em vez do `return null`.
 *
 * (O cursor de volume continua a desaparecer: aí não há nada a ensinar — um
 * volume sem áudio a correr não tem estado nenhum para mostrar.)
 *
 * O botão é um interruptor e diz em que estado está, porque o modo privado é o
 * género de coisa que se liga sem dar por ela e depois se fala para o vazio a
 * achar que a sala ouve — daí também o aviso fixo no rodapé da voz.
 */
function PrivateVoice({ userId, isSelf }: { userId: string; isSelf: boolean }) {
  const snapshot = useVoiceSnapshot();
  if (isSelf) return null;

  const sameCall = snapshot.participants.some((p) => p.identity === userId && !p.isLocal);
  const active = snapshot.privateTo === userId;

  return (
    <button
      type="button"
      className={`user-card__action${active ? " user-card__action--on" : ""}`}
      disabled={!sameCall}
      onClick={() => void voiceEngine.setPrivateVoice(active ? null : userId)}
    >
      {active ? <IconLock /> : <IconMic />}
      {active ? "Terminar voz privada" : "Voz privada"}
      {/* A razão vai DENTRO do botão e não num `title`: um tooltip não existe no
          telemóvel e não se lê sem passar o rato — precisamente as duas
          situações em que a pessoa está a procurar o botão. */}
      {sameCall ? null : <em className="user-card__action-note">só na mesma chamada</em>}
    </button>
  );
}

/**
 * O cursor de volume — e a regra de quando ele existe: SÓ quando estamos os
 * dois na mesma chamada.
 *
 * A pergunta responde-se no cliente e sem margem: o motor sabe quem está na
 * MINHA sala e a identidade de cada um É o id do utilizador. Fora de uma call
 * o cursor não teria nada em que mexer, e mostrá-lo desligado seria prometer
 * um controlo que não faz nada.
 *
 * O valor guardado é MEU e vive no `localStorage` (ver `userVolumes.ts`): não
 * viaja para os outros nem para o servidor.
 */
function UserVolume({ userId, isSelf }: { userId: string; isSelf: boolean }) {
  const snapshot = useVoiceSnapshot();
  const [volume, setVolume] = useState(() => voiceEngine.getUserVolume(userId));

  const sameCall = snapshot.participants.some((p) => p.identity === userId && !p.isLocal);
  // Baixar-me a mim próprio não existe: eu não me oiço.
  if (isSelf || !sameCall) return null;

  const percent = Math.round((volume / VOLUME_MAX) * 100);

  return (
    <label className="user-card__volume">
      <span className="user-card__volume-head">
        {volume === 0 ? <IconSpeakerOff /> : <IconSpeaker />}
        Volume
        <em>{percent}%</em>
      </span>
      <input
        type="range"
        min={0}
        max={100}
        step={1}
        value={percent}
        aria-label="Volume desta pessoa"
        onChange={(event) => {
          // Aplica-se a cada movimento, não ao largar: ajustar volume é ouvir o
          // resultado enquanto se arrasta — sem isso a pessoa arrasta às cegas.
          setVolume(voiceEngine.setUserVolume(userId, (Number(event.target.value) / 100) * VOLUME_MAX));
        }}
      />
    </label>
  );
}
